import type { ChangeEvent, FocusEvent, MouseEvent } from "react";
import { memo, useRef, useState } from "react";
import clsx from "clsx";

import { FormInputBox, FormInputIcon } from "../formControl";
import mainClasses from "../styles.module.scss";

import type { IAutoCompleteProps } from "./types";
import localClasses from "./styles.module.scss";

import { Typography } from "@/components/atoms/typography";
import { useReadOnly, useResizeObserver } from "@/hooks";
import {
  InputMenuItemPopover,
  InputPopover,
  InputPopoverContent,
  useInputPopoverContext,
} from "@/components/organisms/inputPopover";

export const AutoComplete = memo(function AutoComplete(
  props: IAutoCompleteProps,
) {
  const { disabled: propsDisabled, readOnly: propsReadOnly, ...rest } = props;
  const readOnly = useReadOnly(propsReadOnly);
  const disabled = propsDisabled || rest.isLoading;

  return (
    <InputPopover
      ariaRole={"listbox"}
      disabled={disabled || readOnly}
      listNavLoop={false}
    >
      <AutocompleteInput {...rest} disabled={disabled} readOnly={readOnly} />
    </InputPopover>
  );
});

export function AutocompleteInput(props: IAutoCompleteProps) {
  const {
    options,
    value = { key: "", value: "" },
    onChange,
    readOnly,
    disabled,
    onFocus,
    isLoading,
    isError,
    onBlur,
    validation,
    // TODO: remove this props. It causes lagging.
    onInputChange = () => {},
    placeholder,
    onMouseDown,
    ...rest
  } = props;

  const {
    open,
    setOpen,
    activeIndex,
    refs,
    getReferenceProps,
    setActiveIndex,
  } = useInputPopoverContext();

  const [inputValue, setInputValue] = useState("");
  const [_isInputIsFocused, setIsInputIsFocused] = useState(false);
  const isInputIsFocused = _isInputIsFocused && !readOnly;
  const inputRef = useRef<HTMLInputElement>(null);

  useResizeObserver({ elementRef: inputRef });

  const items = options?.filter(
    (item) => item?.value?.toLowerCase()?.includes(inputValue?.toLowerCase()),
  );

  // is there any selected value or input is empty
  const hasSelectedValue = !!value?.key && value?.key !== "";

  // can user clear or modify the value or not
  const canClearItem =
    hasSelectedValue &&
    !readOnly &&
    !disabled &&
    !isLoading &&
    !isError &&
    !isInputIsFocused;

  const getInputPlaceholder = () => {
    if (hasSelectedValue) return isInputIsFocused ? value?.value : undefined;
    return placeholder;
  };

  const inputPlaceholder = getInputPlaceholder();

  const focusOnInput = () => inputRef.current?.focus();

  function handleItemSelection() {
    if (activeIndex !== null) {
      const activeItem = items[activeIndex];

      onChange?.(activeItem);
      setInputValue(activeItem.value);
      setOpen(false);
    }
  }

  function inputOnChange(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;

    setInputValue(value);
    onInputChange(value);
  }

  function inputOnFocus(event: FocusEvent<HTMLInputElement, Element>) {
    setOpen(true);
    setIsInputIsFocused(true);
    onFocus?.(event);
  }
  function inputOnBlur(event: FocusEvent<HTMLInputElement, Element>) {
    setInputValue("");
    setIsInputIsFocused(false);
    // setOpen(false);
    onBlur?.(event);
  }
  function inputOnMouseDown(
    event: MouseEvent<HTMLInputElement, globalThis.MouseEvent>,
  ) {
    event.stopPropagation();
    setOpen(true);
    onMouseDown?.(event);
  }

  return (
    <>
      <FormInputBox
        ref={refs.setReference}
        disabled={!!disabled}
        readOnly={!!readOnly}
        validation={validation}
        onMouseDown={(event) => {
          event.preventDefault();
          focusOnInput();
        }}
      >
        <div className={mainClasses.inputContainer}>
          <div className={clsx(["position-relative", "w-100", "d-flex"])}>
            {!isInputIsFocused && hasSelectedValue && (
              <div
                style={{ maxWidth: `${inputRef.current?.clientWidth}px` }}
                className={clsx([mainClasses.chip, localClasses.chip])}
              >
                <Typography
                  htmlAttribute={{ title: value?.value }}
                  createSpan
                  ellipsis
                >
                  {value?.value}
                </Typography>
              </div>
            )}

            <input
              {...getReferenceProps({
                "aria-autocomplete": "list",
                onKeyDown(event) {
                  switch (event.key) {
                    case "Enter":
                      // prevent form submitting
                      event.preventDefault();
                      handleItemSelection();
                      break;

                    case "Tab":
                      setOpen(false);
                      break;
                    default:
                      if (event.key.length === 1 && !open) setOpen(true);
                      break;
                  }
                },
              })}
              className={clsx([mainClasses.input, "w-100"])}
              readOnly={readOnly}
              disabled={disabled}
              ref={inputRef}
              value={inputValue || ""}
              autoComplete={"off"}
              placeholder={inputPlaceholder}
              onChange={inputOnChange}
              onFocus={inputOnFocus}
              onBlur={inputOnBlur}
              onMouseDown={inputOnMouseDown}
              {...rest}
            />
          </div>
        </div>
        <FormInputIcon
          iconName={canClearItem ? "CloseCircle" : "Magnifer"}
          type={canClearItem ? "actionButton" : "bgIcon"}
          onMouseDown={(event) => {
            if (canClearItem) {
              // prevent to focus out input
              event.preventDefault();
              // prevent to call `FormControlBox` onMouseDownEvent
              event.stopPropagation();
            }
          }}
          onClick={() => {
            // clear the value
            if (canClearItem) onChange?.({ key: "", value: "", flag: "" });
          }}
          {...{ isLoading, isError, disabled, readOnly }}
        />
      </FormInputBox>
      <InputPopoverContent>
        {items.map((item, index) => {
          return (
            <InputMenuItemPopover
              key={item.key}
              index={index}
              onClick={handleItemSelection}
              selected={item.key === value?.key}
              onMouseDown={
                // cause we don't want to lose input's focus on icon clicking event
                (event) => {
                  event.preventDefault();
                  setActiveIndex(index);
                }
              }
            >
              {item.value}
            </InputMenuItemPopover>
          );
        })}
      </InputPopoverContent>
    </>
  );
}
